import React, { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import { Actions as ActionsIcon, Edit as EditIcon, Delete as DeleteIcon } from "@/components/icons";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type { TableColumn } from "@/types/component";
import { personnelGroupsApi } from "../services/personnel-groups.api";
import type { PersonnelGroup } from "../types/personnel-groups.types";
import CreateGroupModal from "../components/CreateGroupModal";
import styles from "./PersonnelGroupsPage.module.scss";

const PersonnelGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canView = hasPermission(PERMISSIONS.PERSONNEL_GROUPS_VIEW);
  const canCreate = hasPermission(PERMISSIONS.PERSONNEL_GROUPS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.PERSONNEL_GROUPS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.PERSONNEL_GROUPS_DELETE);
  const [data, setData] = useState<PersonnelGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PersonnelGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PersonnelGroup | null>(null);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const fetchData = useCallback(async () => {
    if (!canView) {
      setData([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const res = await personnelGroupsApi.list({ page: currentPage, limit: pageSize, search });
      setData(res.items);
      setTotal(res.totalCount);
    } finally {
      setLoading(false);
    }
  }, [canView, currentPage, pageSize, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: TableColumn<PersonnelGroup>[] = [
    { header: t("personnelGroups.groupName", "Grup Adı"), accessor: "name", className: styles.nameColumn },
    {
      header: t("personnelGroups.description", "Açıklama"),
      accessor: (row) => row.description || "-",
      className: styles.descriptionColumn,
    },
  ];

  const renderActions = (row: PersonnelGroup) => {
    if (!canUpdate && !canDelete) return null;
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;
    const menuContent = (
      <div className={styles.actionMenu}>
        {canUpdate && (
          <button
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              setEditing(row);
              setModalOpen(true);
              handleDropdownToggle(rowId, false);
            }}
          >
            <EditIcon width={20} height={20} />
            {t("buttons.edit")}
          </button>
        )}
        {canDelete && (
          <button
            className={`${styles.actionMenuItem} ${styles.error}`}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(row);
              handleDropdownToggle(rowId, false);
            }}
          >
            <DeleteIcon width={20} height={20} />
            {t("buttons.delete")}
          </button>
        )}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("personnelGroups.title")} />
      <PageHeader
        searchValue={search}
        onSearch={(v) => {
          setSearch(v);
          setCurrentPage(1);
        }}
        onAddNew={canCreate ? () => {
          setEditing(null);
          setModalOpen(true);
        } : undefined}
        addButtonLabel={canCreate ? t("personnelGroups.addButton", "Yeni Grup") : undefined}
      />
      <Table
        columns={columns}
        data={data}
        isLoading={loading}
        getRowId={(r) => r.id}
        onRowClick={canView ? (row) => navigate(`/dashboard/personnel-groups/${row.id}`) : undefined}
        totalRecords={total}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
      />
      <CreateGroupModal
        isOpen={modalOpen}
        initial={editing ? { name: editing.name, description: editing.description } : null}
        onClose={() => setModalOpen(false)}
        onSubmit={async (payload) => {
          try {
            if (editing) await personnelGroupsApi.update(editing.id, payload);
            else await personnelGroupsApi.create(payload);
            setModalOpen(false);
            fetchData();
          } catch (error) {
            const status = (error as AxiosError)?.response?.status;
            if (status === 409) {
              toast.error(t("personnelGroups.duplicateName", "Bu isimde bir personel grubu zaten var."));
              return;
            }
            toast.error(t("personnelGroups.operationFailed", "İşlem başarısız"));
          }
        }}
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          await personnelGroupsApi.remove(deleteTarget.id);
          setDeleteTarget(null);
          fetchData();
        }}
        title="Grubu sil"
        message="Bu grubu silmek istediğinize emin misiniz?"
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default PersonnelGroupsPage;
